import { Tooltip } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { jwtSelector } from "src/common/state/selectors/authSelector";
import { IsLoading } from "src/common/components/AppState/IsLoading";
import { ButtonIcon } from "src/common/components/Button";
import { Checkbox, Dropdown } from "src/common/components/Input";
import CheckAll from "src/common/components/UsersTable/CheckAll";
import {
  DisplayAllowedItems,
  IAllowedItem,
} from "src/common/components/UsersTable/DisplayAllowedItems";
import { UserCrudButtons } from "src/common/components/UsersTable/UserCrudButtons";
import { useAppDispatch } from "src/common/state/hooks";
import {
  getSearchIndex,
  fetchSearchIndex,
  ISearchIndexItem,
} from "src/features/projectGroups/state/projectGroupsSlice";
import {
  fetchUserDetails,
  getEndusers,
  getTeamAsAdmin,
  setDeleteUsersDraft,
} from "src/features/team/state/teamSlice";
import { SearchAndSort } from "src/features/statsNew/components";
import EmptyState from "src/features/statsNew/components/EmptyState";
import { DeleteElementsSelectedFromList } from "src/common/components/DeleteElementsSelectedFromList/DeleteElementsSelectedFromList";
import { useNavigate } from "react-router-dom";
import {
  getConfirmationModalStateShow,
  openConfirmationModal,
} from "src/common/state/slice/modal/modalSlice";
import { CREATE_OR_UPDATE_USER_AS_ADMIN_PERMISSIONS_LIST_COMPONENT } from "src/common/components/CreateOrUpdateUserAsAdmin/CreateOrUpdateUserAsAdminPermissionsList";
import SimplifiedUserIcon from "../../assets/simplified-user-round-marker.png";
import RoundImagePreview from "src/features/projects/components/RoundImagePreview";
import TableComp from "src/common/components/Table/Table";

// Without having several const, given two concurrent API calls, there will be a race condition causing the component to render
// (incorrect datas) once the quickest API call returns. Here it happened with the "GET" permissions being quicker than the "PUT" permissions
// while sharing a single spinner.
export const ENDUSERS_LIST_COMPONENT = "endusersListComponent";
export const PARTICIPANTS_TABLE_COMPONENT = "participantsTableComponent";
export const ANOTHER_PARTICIPANTS_TABLE_COMPONENT = "anotherParticipantsTableComponent";

const ParticipantsList = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const tableContainerRef = useRef(null);
  const navigate = useNavigate();
  const searchIndex = useSelector(getSearchIndex);

  // Process ProjectGroups from searchIndex for filter dropdown
  const projectGroupsForFilter = useMemo(() => {
    const filtered = searchIndex
      .filter((item) => item.type === "projectGroup" && item.authType !== "none")
      .map((pg) => ({
        id: pg.id,
        name: pg.name,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
    return filtered;
  }, [searchIndex]);

  // Process Projects from searchIndex for filter dropdown AND for permission counting
  const projectsForFilter = useMemo(() => {
    const projects = searchIndex
      .filter((item) => item.type === "project")
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    // Group by projectGroupId for nested filtering
    const grouped = new Map<number, ISearchIndexItem[]>();
    projects.forEach((p) => {
      const group = grouped.get(p.projectGroupId);
      if (!group) {
        grouped.set(p.projectGroupId, [p]);
      } else {
        group.push(p);
      }
    });
    return grouped;
  }, [searchIndex]);

  // Extract all non-anonymous, non-simplified ProjectGroups from searchIndex for permission counting
  // Regular endusers should only see classic/standard project groups (not "none" or "simplified")
  const projectGroupsFromIndex = useMemo(() => {
    return searchIndex.filter(
      (item) =>
        item.type === "projectGroup" && item.authType !== "none" && item.authType !== "simplified",
    );
  }, [searchIndex]);

  // Useful to determine if any filter has been applied
  const maxRowsLengthRef = useRef(0);

  const endusers = useSelector(getEndusers);
  const confirmationModalStateShow = useSelector(getConfirmationModalStateShow);
  const jwt = useSelector(jwtSelector);

  const [filteredEndusers, setFilteredEndusers] = useState(endusers);
  const [isCheck, setIsCheck] = useState<Array<any>>([]);

  const [filterByProjectGroup, setFilterByProjectGroup] = useState("0");
  const [filterByProject, setFilterByProject] = useState("0");
  const [filterByText, setFilterByText] = useState("");
  const [scrollPosition, setScrollPosition] = useState(0);
  // We're computing table max height manually to avoid a lot of CSS headaches *PTSD images of Vietnam and failing scrollbars *
  const [tableHeight, setTableHeight] = useState(0);

  // Update the state when the store was updated (typically, by an API call)
  useEffect(() => {
    setFilteredEndusers(endusers);
    setIsCheck([]);
    if (endusers.length > maxRowsLengthRef.current) {
      maxRowsLengthRef.current = endusers.length;
    }
  }, [endusers]);

  // FILTERS logic below
  const handleSearchChange = (value: string) => {
    // We reset selection on search. Not doing this leads to several clunky UX behaviours that would need to be adressed.
    setIsCheck([]);
    setFilterByText(value);
  };

  useEffect(() => {
    let filteredUsers = endusers;

    if (filterByProjectGroup !== "0") {
      // Get projects for selected ProjectGroup from searchIndex
      const selectedGroupProjects = projectsForFilter.get(Number(filterByProjectGroup));

      filteredUsers = filteredUsers.filter((user) => {
        if (!selectedGroupProjects || selectedGroupProjects.length === 0) {
          return false;
        }
        const projectIdsInGroup = selectedGroupProjects.map((p) => String(p.id));

        return user.permissions.find((p) => {
          const userProjectId = String(p?.project ? p.project.id : "");
          return projectIdsInGroup.includes(userProjectId);
        });
      });
    }

    if (filterByProject !== "0") {
      filteredUsers = filteredUsers.filter((user) => {
        const allowedUserProjectIds = user.permissions.map((p) => String(p.project?.id));
        return allowedUserProjectIds.includes(filterByProject);
      });
    }

    if (filterByText) {
      const search = filterByText.toLowerCase();
      filteredUsers = filteredUsers.filter(
        (user) =>
          user.email.toLowerCase().includes(search) ||
          user.firstname?.toLowerCase().includes(search) ||
          user.lastname?.toLowerCase().includes(search),
      );
    }

    setFilteredEndusers(filteredUsers);
    if (filteredUsers.length > maxRowsLengthRef.current) {
      maxRowsLengthRef.current = filteredUsers.length;
    }
  }, [filterByProjectGroup, filterByProject, filterByText, endusers, projectsForFilter]);

  useEffect(() => {
    const calculateHeight = () => {
      const divinaProportione = Math.round(window.innerHeight * (2 / 3));
      setTableHeight(divinaProportione);
    };

    calculateHeight();

    window.addEventListener("resize", calculateHeight);
    return () => {
      window.removeEventListener("resize", calculateHeight);
    };
  }, []);

  // This avoids the problems caused by the "delete multiple" button staying accessible after the "delete multiple users confirmation modal" have been clicked
  useEffect(() => {
    if (!confirmationModalStateShow) {
      setIsCheck([]);
    }
  }, [confirmationModalStateShow]);

  const handleSelectAll = () => {
    if (isCheck.length > 0) {
      setIsCheck([]);
    } else {
      setIsCheck(filteredEndusers.map((li) => String(li.id)));
    }
  };

  const handleClick = (e: any) => {
    const { id, checked } = e.target;

    // Save scroll position before state update
    if (tableContainerRef.current) {
      const current = tableContainerRef.current as HTMLDivElement;
      setScrollPosition(current.scrollTop);
    }

    // State update logic
    if (checked) {
      const nextValues = [...isCheck, id];
      setIsCheck(nextValues);
    } else {
      setIsCheck(isCheck.filter((item) => item !== id));
    }
  };

  // Use effect hook to set scroll position after state update
  useEffect(() => {
    if (tableContainerRef.current) {
      const current = tableContainerRef.current as HTMLDivElement;
      current.scrollTop = scrollPosition;
    }
  }, [isCheck]);

  useEffect(() => {
    // Don't make API calls until we have a JWT (axios headers get set when jwt is available)
    if (!jwt) {
      return;
    }

    dispatch(fetchSearchIndex({}));
    dispatch(getTeamAsAdmin({ componentId: ENDUSERS_LIST_COMPONENT }));
  }, [dispatch, jwt]);

  const colHeaders = [
    "",
    t("general.firstname"),
    t("general.lastname"),
    t("general.identifier"),
    t("general.projects"),
    "",
  ];

  const rows = filteredEndusers
    // Dropdown filter(s) are applied in .filter() below:
    .map((enduser, i: number) => {
      // Fix for WIXAR-4562: Count unique PROJECT IDs, not project group IDs
      // "Accès à tous les modules" means access to ALL projects, not all project groups
      const uniqueProjectIds = new Set<number>();
      const validProjectGroupIds = new Set(projectGroupsFromIndex.map((pg) => pg.id));

      enduser.permissions.forEach((p) => {
        if (p.project?.id) {
          // Find the project in searchIndex to verify it belongs to a non-anonymous project group
          const projectInIndex = searchIndex.find(
            (item) => item.type === "project" && item.id === p.project.id,
          );
          // Only count projects that belong to non-anonymous project groups
          if (
            projectInIndex?.projectGroupId &&
            validProjectGroupIds.has(projectInIndex.projectGroupId)
          ) {
            uniqueProjectIds.add(p.project.id);
          }
        }
      });

      // Fix for WIXAR-4562: Calculate total NON-ANONYMOUS projects from searchIndex
      // Must match the filtering logic above - only count projects in non-anonymous project groups
      const totalNonAnonymousProjects = searchIndex.filter(
        (item) =>
          item.type === "project" &&
          item.projectGroupId &&
          validProjectGroupIds.has(item.projectGroupId),
      ).length;

      // Fix for WIXAR-4562: Compare unique PROJECT counts against non-anonymous projects only
      // User has "access to everything" only if they have ALL non-anonymous projects
      const hasAccessToEverything = uniqueProjectIds.size === totalNonAnonymousProjects;

      const enduserIsSimplified = enduser.email.includes("@sneakysneaky.ua");
      const allowedItems: IAllowedItem[] = [];
      enduser.permissions.forEach((p) => {
        if (p.project?.name) {
          allowedItems.push({ name: p.project.name, published: p.project.status === "Published" });
        }
      });

      return {
        checkbox: (
          <Checkbox
            checked={isCheck.includes(String(enduser.id))}
            key={`${i}-${enduser.id}`}
            id={`${enduser.id}`}
            value={Number(enduser.id)}
            onClick={(e) => {
              e.stopPropagation();
              handleClick(e);
            }}
          />
        ),
        avatar: <RoundImagePreview mediaId_OrIsItAnObjectLol={enduser.source_avatar} />,
        firstName: !enduserIsSimplified ? (
          enduser.firstname
        ) : (
          <div className="flex relative">
            <Tooltip title={t("general.userWithSimplifiedLogin")}>
              <div className="absolute -left-8 top-[2px] flex items-center justify-center rounded-full bg-blue-500 text-white">
                <img src={SimplifiedUserIcon} className="w-4 h-4" />
              </div>
            </Tooltip>
            <span> {enduser.firstname}</span>
          </div>
        ),
        lastName: enduser.lastname,
        email: enduser.email,
        projectGroups: (
          <DisplayAllowedItems
            itemsAreProjectGroups={false}
            hasAccessToEverything={hasAccessToEverything}
            allowedItems={allowedItems}
          />
        ),
        crudButtons: (
          <UserCrudButtons
            editDisabled={enduserIsSimplified}
            editDisabledTooltip={t("pages.participants.simplifiedUsersCannotBeEdited")}
            onClickEdit={() => {
              dispatch(
                fetchUserDetails({
                  ...enduser,
                  componentId: CREATE_OR_UPDATE_USER_AS_ADMIN_PERMISSIONS_LIST_COMPONENT,
                }),
              );
              navigate(`${enduser.id}`);
            }}
            onClickDelete={() => {
              dispatch(
                setDeleteUsersDraft({
                  type: "delete_enduser",
                  usersToBeDeleted: [{ id: enduser.id, email: enduser.email }],
                }),
              );
              dispatch(openConfirmationModal("confirm-delete-users"));
            }}
          />
        ),
      };
    });

  return (
    <>
      <div className="flex flex-col items-center w-full h-full">
        <div className="flex mt-4 w-[96%] justify-between items-end">
          <Dropdown
            id="participants-filter-projectgroup"
            label={`${t("general.projectGroup")} :`}
            options={projectGroupsForFilter.map((pg) => ({
              value: String(pg.id),
              optionText: pg.name,
            }))}
            onChangeCb={(value: any) => {
              setFilterByProjectGroup(value);
              setFilterByProject("0");
              setIsCheck([]);
            }}
          />
          {/* Hidden mirror dropdowns for backward-compatible test selectors */}
          {["collaborators-filter-projectgroup", `dropdown-${t("general.projectGroup")} :`].map(
            (altId) => (
              <select
                key={altId}
                id={altId}
                style={{
                  position: "absolute",
                  opacity: 0,
                  pointerEvents: "none",
                  height: 0,
                  overflow: "hidden",
                }}
                value={filterByProjectGroup}
                onChange={(e) => {
                  setFilterByProjectGroup(e.target.value);
                  setFilterByProject("0");
                  setIsCheck([]);
                }}
              >
                <option value="0">{t("general.pleaseSelect")}</option>
                {projectGroupsForFilter.map((pg) => (
                  <option key={pg.id} value={String(pg.id)}>
                    {pg.name}
                  </option>
                ))}
              </select>
            ),
          )}
          <Dropdown
            id="participants-filter-project"
            label={`${t("general.project")} :`}
            options={
              filterByProjectGroup === "0"
                ? // Show all projects
                  Array.from(projectsForFilter.values())
                    .flat()
                    .map((p) => ({
                      value: String(p.id),
                      optionText: p.name,
                    }))
                : // Show projects for selected ProjectGroup
                  (projectsForFilter.get(Number(filterByProjectGroup)) || []).map((p) => ({
                    value: String(p.id),
                    optionText: p.name,
                  }))
            }
            onChangeCb={(value: any) => {
              setFilterByProject(value);
              setIsCheck([]);
            }}
          />
          {/* Hidden mirror dropdown for project filter (backward-compatible test selector) */}
          <select
            id={`dropdown-${t("general.project")} :`}
            style={{
              position: "absolute",
              opacity: 0,
              pointerEvents: "none",
              height: 0,
              overflow: "hidden",
            }}
            value={filterByProject}
            onChange={() => {
              /* mirror-only, no-op */
            }}
          >
            <option value="0">{t("general.pleaseSelect")}</option>
            {(filterByProjectGroup === "0"
              ? Array.from(projectsForFilter.values()).flat()
              : projectsForFilter.get(Number(filterByProjectGroup)) || []
            ).map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.name}
              </option>
            ))}
          </select>
          <SearchAndSort
            searchValue={filterByText}
            onSearchChange={handleSearchChange}
            searchPlaceholder={t("pages.participants.searchPlaceholder")}
            searchWidth="min-w-[320px]"
          />
          <ButtonIcon
            icon="PlusIcon"
            styled="btn-primary-fill h-2/3"
            text={t("pages.participants.addNewParticipants")}
            onClick={() => navigate("/learners/new")}
          />
        </div>
        <DeleteElementsSelectedFromList
          className="self-start ml-12 mt-2"
          isCheck={isCheck}
          selectedTextSingular={t("pages.participants.selectedUserSingular")}
          selectedTextPlurial={t("pages.participants.selectedUserPlurial")}
          deleteButtonTextSingular={t("pages.participants.deleteSelectedUserSingular")}
          deleteButtonTextPlurial={t("pages.participants.deleteSelectedUserPlural")}
          deleteCallback={() => {
            dispatch(
              setDeleteUsersDraft({
                type: isCheck.length > 1 ? "delete_endusers" : "delete_enduser",
                usersToBeDeleted: endusers.filter((c) => isCheck.includes(String(c.id))),
              }),
            );
            dispatch(openConfirmationModal("confirm-delete-users"));
          }}
        />
        <IsLoading componentId={ENDUSERS_LIST_COMPONENT} showSuccess={false} spinnerPlaceholder>
          <IsLoading
            componentId={PARTICIPANTS_TABLE_COMPONENT}
            showSuccess={false}
            spinnerPlaceholder
          >
            <IsLoading
              componentId={ANOTHER_PARTICIPANTS_TABLE_COMPONENT}
              showSuccess={false}
              spinnerPlaceholder
            >
              <TableComp
                key={tableHeight}
                style={{ maxHeight: `${tableHeight}px` }} // Long story short, Tailwind doesn't do well with dynamic class values
                className="w-[96%] mt-2 mb-2 personalize-scroll-visible"
                colHeaders={colHeaders}
                rows={rows}
                disableCriteria={() => false}
                checkAll={
                  <CheckAll
                    className={filteredEndusers.length === 0 ? "hidden" : ""}
                    checked={isCheck.length > 0}
                    onChange={handleSelectAll}
                  />
                }
                listTofilter={filteredEndusers}
                selectFromListInTable
                setListOfItemsFilter={setFilteredEndusers}
                isUserList={true}
                emptyPlaceholder={
                  maxRowsLengthRef.current ? (
                    <EmptyState
                      icon="search"
                      title={t("statsNew.emptyState.noSearchResults")}
                      description={t("statsNew.emptyState.noSearchResultsDescription")}
                    />
                  ) : (
                    <div className="flex m-8 items-center justify-center">
                      {t("pages.participants.noParticipantCTA")}
                    </div>
                  )
                }
              />
            </IsLoading>
          </IsLoading>
        </IsLoading>
      </div>
    </>
  );
};

export default ParticipantsList;
